import React from 'react';
import { motion } from 'framer-motion';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  Shield, 
  Smartphone, 
  Wrench, 
  Code, 
  Search,
  Cpu,
  Database,
  Lock,
  Bug,
  Target
} from 'lucide-react';

const Skills = ({ data }) => {
  const iconMap = {
    Shield: Shield,
    Smartphone: Smartphone, 
    Wrench: Wrench,
    Code: Code,
    Search: Search,
    Cpu: Cpu,
    Database: Database,
    Lock: Lock,
    Bug: Bug,
    Target: Target
  };

  const colorMap = {
    green: 'text-green-400 border-green-400/30 bg-green-400/10',
    cyan: 'text-cyan-400 border-cyan-400/30 bg-cyan-400/10',
    red: 'text-red-400 border-red-400/30 bg-red-400/10',
    yellow: 'text-yellow-400 border-yellow-400/30 bg-yellow-400/10',
    purple: 'text-purple-400 border-purple-400/30 bg-purple-400/10'
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <section id="skills" className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center mb-4">
            <div className="h-px bg-gradient-to-r from-transparent via-green-400 to-transparent flex-1 max-w-xs" />
            <Code className="w-8 h-8 text-green-400 mx-4" />
            <div className="h-px bg-gradient-to-r from-transparent via-green-400 to-transparent flex-1 max-w-xs" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-green-400 mb-4">
            Technical Arsenal
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Comprehensive cybersecurity skills and tools mastery for identifying, exploiting, and securing digital systems.
          </p>
        </motion.div>

        {/* Skills Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8"
        >
          {data.categories.map((category, index) => {
            const IconComponent = iconMap[category.icon] || Shield;
            const colorClasses = colorMap[category.color] || colorMap.green;
            
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <Card className="bg-gray-800/50 border-gray-700 backdrop-blur-sm hover:border-green-400/50 transition-all duration-300 h-full">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-3">
                      <div className={`p-3 rounded-lg border ${colorClasses}`}>
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <span className="text-xl font-semibold text-white">
                        {category.name}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {category.skills.map((skill, skillIndex) => (
                        <motion.div
                          key={skillIndex}
                          className="space-y-2"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ 
                            delay: skillIndex * 0.1,
                            duration: 0.5
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-gray-300 font-medium">
                              {skill.name}
                            </span>
                            <Badge 
                              variant="outline" 
                              className={`${colorClasses} border-opacity-50 text-xs`}
                            >
                              {skill.level}%
                            </Badge>
                          </div>
                          <div className="relative">
                            <Progress 
                              value={skill.level} 
                              className="h-2 bg-gray-700" 
                            />
                            <motion.div
                              className="absolute inset-0 bg-gradient-to-r from-green-400 to-cyan-400 rounded-full"
                              initial={{ width: 0 }}
                              whileInView={{ width: `${skill.level}%` }}
                              viewport={{ once: true }}
                              transition={{ 
                                delay: skillIndex * 0.1 + 0.5,
                                duration: 1,
                                ease: 'easeOut'
                              }}
                            />
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Security Focus Areas */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-16 text-center"
        >
          <h3 className="text-2xl font-bold text-white mb-8">Security Domains</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'OWASP Top 10',
              'Mobile Security',
              'Network Security',
              'Web Application Security',
              'API Security',
              'Cloud Security',
              'Digital Forensics',
              'Malware Analysis',
              'Red Teaming',
              'Blue Teaming'
            ].map((domain, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ scale: 1.05 }}
              >
                <Badge 
                  className="bg-gray-800 text-green-400 border-green-400/30 hover:bg-green-400/10 transition-colors duration-300 px-4 py-2 text-sm"
                >
                  {domain}
                </Badge>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;